import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@vimaana/ui";

import type { ProjektLage, ProjektPhaseZeile } from "../lib/projektLage";

/**
 * Phase, Grund und nächster Schritt eines Projekts. Der Server schreibt beim
 * Lesen den Projektstatus mit (`status_geschrieben`) — dann sind Projektkopf und
 * Projektliste veraltet und werden nachgeladen.
 *
 * Schlüssel unter ["projekt", id], damit jede Änderung am Projekt die Lage mitzieht.
 */
export function useProjektLage(projektId?: string) {
  const qc = useQueryClient();
  const q = useQuery<ProjektLage>({
    queryKey: ["projekt", projektId, "lage"],
    queryFn: () => apiGet<ProjektLage>(`/api/kontor/projekt/${projektId}/lage`),
    enabled: !!projektId,
    staleTime: 10_000,
  });
  const geschrieben = q.data?.status_geschrieben;
  useEffect(() => {
    if (!geschrieben || !projektId) return;
    qc.invalidateQueries({ queryKey: ["projekt", projektId], exact: true });
    qc.invalidateQueries({ queryKey: ["projekte"] });
  }, [geschrieben, projektId, qc]);
  return q;
}

/** Nach Beleg-, Termin- oder Statusänderungen aus dem Kopf heraus: Lage neu rechnen lassen. */
export function useLageNeu(projektId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["projekt", projektId] });
    qc.invalidateQueries({ queryKey: ["projekte"] });
  };
}

/**
 * Antwort auf „Ist die Arbeit fertig, oder braucht es noch Nacharbeit?" (Benjamin,
 * 20.10.2026, Fall Gröger). `noetig: false` macht das Projekt abrechnungsbereit,
 * bis ein späterer Einsatz vorbei ist.
 */
export function useNacharbeit(projektId: string) {
  const neu = useLageNeu(projektId);
  return useMutation({
    mutationFn: (noetig: boolean) => apiPost<ProjektLage>(`/api/kontor/projekt/${projektId}/nacharbeit`, { noetig }),
    onSuccess: neu,
  });
}

/**
 * Phase je laufendem Projekt, ein Aufruf für die ganze Liste (Projektliste, Planer).
 * Nur lesend — den Status schreibt allein die Lage beim Öffnen eines Projekts.
 * Schlüssel unter ["projekte"], damit Änderungen an Projekten die Liste mitziehen.
 */
export function useProjektPhasen(enabled = true) {
  return useQuery<Record<string, ProjektPhaseZeile>>({
    queryKey: ["projekte", "phasen"],
    queryFn: async () => {
      const d = await apiGet<{ projekte: ProjektPhaseZeile[] }>("/api/kontor/projekte/phasen");
      return Object.fromEntries(d.projekte.map((z) => [z.projekt_id, z]));
    },
    enabled,
    staleTime: 30_000,
  });
}
